import { ArrowRightIcon, Check, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { BorderedRadioButton, Button, RadioGroup, TextLink, useBreakpoint } from 'slab'

import { emitByopNoPlansStateDirectionsPageViewEvent } from '../events'
import { useOnboarding } from '../OnboardingContext'
import { type StepComponentProps } from '../types'

type NoPlansStateDirectionsProps = StepComponentProps & {
  onContinue: () => void
}

type PlanDecision = 'valid_plan' | 'keep_invalid_plan'

export function NoPlansStateDirections ({
  onContinue,
  navigateToSpecificStep,
  userId,
}: NoPlansStateDirectionsProps) {
  const { breakpointIsAtLeast } = useBreakpoint()
  const { onboardingData } = useOnboarding()
  const [planDecision, setPlanDecision] = useState<PlanDecision | null>(null)

  useEffect(() => {
    if (onboardingData && userId) {
      emitByopNoPlansStateDirectionsPageViewEvent(userId, onboardingData)
    }
  }, [onboardingData, userId])

  const memberState = onboardingData?.member?.state || ''
  const enrollUrl = onboardingData?.member?.enroll_url || ''

  const onClickContinue = () => {
    if (planDecision === 'valid_plan') {
      onContinue()
    } else if (planDecision === 'keep_invalid_plan') {
      navigateToSpecificStep?.('opted_out')
    }
  }

  let continueButtonLabel = 'Continue'
  if (planDecision === 'keep_invalid_plan') {
    continueButtonLabel = 'Go to opt out'
  }

  const continueMessage = (
    <span className='body-sm text-gray-500'>
      By continuing, you acknowledge you'll have to give your allowance back if
      it's used on an unsupported kind of plan.
    </span>
  )

  const continueButton = (
    <Button
      size='large'
      variant='primary'
      rightIcon={ArrowRightIcon}
      onClick={onClickContinue}
      width={breakpointIsAtLeast('xs') ? 'auto' : 'full'}
    >
      {continueButtonLabel}
    </Button>
  )

  const fullStateName = {
    SD: 'South Dakota',
    DC: 'Washington D.C.',
  }[memberState]

  return (
    <div
      data-testid='NoPlansStateDirections'
      className='max-w-[44rem]'
    >
      <div className='mb-10'>
        <div>
          <h1 className='heading-lg text-gray-800 mb-5'>Use my own plan</h1>
          <p className='body text-gray-500'>Your employer allowance can be used{' '}
            <TextLink
              target='_blank'
              href='https://support.thatch.com/eligible-medical-plans#eligible-plans-for-individuals-under-65-years-of-age'
            >
              for certain types of plans
            </TextLink>{' '}
            that you purchase yourself, like:
          </p>
          <ul className='list-none mt-2 pb-3 body'>
            <li className='flex items-center mb-2'>
              <Check className='w-4 h-4 mr-1 text-green-400 shrink-0' />
              Individual and family plans purchased without tax credits or
              subsidies
            </li>
            <li className='flex items-center mb-4'>
              <Check className='w-4 h-4 mr-1 text-green-400 shrink-0' />
              Student health insurance
            </li>
          </ul>

          Individual and family plans purchased with tax credits or subsidies
          Employer group plans, including COBRA

          <p className='body text-gray-500'>It can't be used for plans like:</p>
          <ul className='list-none mt-2 pb-3 body'>
            <li className='flex items-center mb-2 last:mb-0'>
              <X className='w-4 h-4 mr-1 text-red-400 shrink-0' />
              Individual and family plans purchased with tax credits or subsidies
            </li>
            <li className='flex items-center mb-2 last:mb-0'>
              <X className='w-4 h-4 mr-1 text-red-400 shrink-0' />
              Employer group plans, including COBRA
            </li>
            <li className='flex items-center mb-2 last:mb-0'>
              <X className='w-4 h-4 mr-1 text-red-400 shrink-0' />
              Government-sponsored plans, like Medicaid, TRICARE, or Veterans Affairs (VA)
            </li>
          </ul>
          <p className='body text-gray-800 mt-3 mb-5'>We don't have any plans available in your area, but you can get a plan on {enrollUrl ? <>the <TextLink color='purple' target='_blank' href={enrollUrl}>{fullStateName} marketplace</TextLink></> : 'your state marketplace'}, then come back and tell us about it.</p>
        </div>
        <div className='pt-3'>
          <RadioGroup gap={3} name='plan-decision'>
            <BorderedRadioButton
              label='I have the right kind of plan'
              value='valid_plan'
              checked={planDecision === 'valid_plan'}
              onChange={() => setPlanDecision('valid_plan')}
            />
            <BorderedRadioButton
              label="I have an invalid plan that I'd like to keep and will forgo my allowance"
              value='keep_invalid_plan'
              checked={planDecision === 'keep_invalid_plan'}
              onChange={() => setPlanDecision('keep_invalid_plan')}
            />
          </RadioGroup>
        </div>
        {planDecision === 'valid_plan' && (
          <div className='mt-8 flex flex-col xs:flex-row gap-4 xs:items-center'>
            {breakpointIsAtLeast('xs') && continueMessage}
            <div className='justify-end'>{continueButton}</div>
            {!breakpointIsAtLeast('xs') && continueMessage}
          </div>
        )}
        {planDecision && planDecision !== 'valid_plan' && (
          <div className='mt-8 flex justify-end'>{continueButton}</div>
        )}
      </div>
    </div>
  )
}
